<script setup>
// Centred narrow auth card per mockups/redesign (login.html, register.html, etc.).
// Renders the bordered shell plus an optional hero (icon/title/subtitle).
// Forms with state-dependent heroes (LoginForm/RegisterForm) render their own
// `.auth-hero` block and use this component as a bare shell.
const props = defineProps({
  // 'logo' = app icon tile, 'mail' = brand-tinted mail tile (register-verify.html),
  // 'check' = success-tinted circle (validate.html), null = no icon (or use #icon slot)
  icon: {
    type: String,
    default: null
  },
  title: {
    type: String,
    default: null
  },
  subtitle: {
    type: String,
    default: null
  }
});
</script>

<template>
  <div class="auth-card">
    <div v-if="icon || title || subtitle || $slots.icon || $slots.hero" class="auth-hero">
      <slot name="icon">
        <div v-if="icon === 'logo'" class="auth-logo">
          <img src="/images/icon_red.png" alt="Tap Leagues"/>
        </div>
        <div v-else-if="icon === 'mail'" class="auth-icon-tile brand">
          <v-icon size="30" icon="mdi-email-outline"/>
        </div>
        <div v-else-if="icon === 'check'" class="auth-icon-tile success">
          <v-icon size="32" icon="mdi-check"/>
        </div>
      </slot>
      <h1 v-if="title">{{ title }}</h1>
      <p v-if="subtitle">{{ subtitle }}</p>
      <slot name="hero"/>
    </div>
    <slot/>
  </div>
</template>

<style lang="scss">
/* Intentionally unscoped: these are the shared auth primitives from the redesign
   mockups, used by content slotted into the card (LoginForm, RegisterForm, pages). */

/* page column for auth screens; padding-top clears the app bar because
   --v-layout-top is broken app-wide (see IMPLEMENTATION-LOG pre-existing issues).
   Doubled-up selector so it reliably beats Vuetify's .v-container padding
   regardless of dev/prod stylesheet order. */
.auth-page.auth-page {
  max-width: 520px;
  padding-top: 64px;
}

.auth-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 28px 22px;
  max-width: 440px;
  margin: 24px auto 0;
}

@media (min-width: 600px) {
  .auth-card {
    padding: 36px 36px 32px;
    margin-top: 40px;
  }
}

.auth-hero {
  text-align: center;
  padding: 0 0 8px;

  h1 {
    font-size: 22px !important;
    font-weight: 800;
    letter-spacing: -0.5px;
    line-height: 1.3;
  }

  p {
    font-size: 12.5px;
    color: rgb(var(--v-theme-textSubtle));
    margin-top: 8px;
    line-height: 1.6;
  }
}

.auth-logo {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.auth-icon-tile {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;

  &.brand {
    background: rgb(var(--v-theme-brandTint));
    color: rgb(var(--v-theme-primary));
  }

  &.success {
    border-radius: 50%;
    background: rgb(var(--v-theme-successTint));
    color: rgb(var(--v-theme-success));
  }
}

.auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0;
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgb(var(--v-theme-borderLight));
  }
}

/* locked/readonly email row (login-password.html, register.html step 2) */
.auth-locked-row {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13.5px;
  color: rgb(var(--v-theme-charcoal));
  text-align: left;

  .email {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .change {
    font-size: 12px;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
    text-decoration: none;
    cursor: pointer;
    flex-shrink: 0;
  }
}

/* small helper line under a field */
.auth-help {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.5;
  margin: 6px 2px 0;
  text-align: left;

  a {
    color: rgb(var(--v-theme-primary));
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
  }
}

/* footer link line */
.auth-foot {
  text-align: center;
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 18px;

  a {
    color: rgb(var(--v-theme-primary));
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
  }
}

/* centred fine-print note (approve-note in the mockups) */
.auth-note {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  text-align: center;
  margin-top: 10px;
  line-height: 1.5;

  a {
    color: rgb(var(--v-theme-primary));
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
  }
}

/* full-width hairline pill (social buttons, secondary block actions) */
.auth-social-btn {
  border-color: rgb(var(--v-theme-borderLight)) !important;
  color: rgb(var(--v-theme-text)) !important;
  font-size: 13.5px;
}
</style>
